<template>
    <div>
        <ForwardRequestModal
            :visible="forwardModalVisible"
            :request="context.request"
            :state="forwardModalState"
            :show-email-settings="showEmailSettings"
            @close="forwardModalVisible = false"
            @submit="submitForwardRequest"
        />

        <b-row class="request-handling-step">
            <b-col
                v-for="option in options"
                :key="option.value"
                lg="4"
                class="mb-1"
            >
                <label
                    :for="option.id"
                    class="request-handling-step__option"
                    :class="{
                        'request-handling-step__option--selected': selectedStrategy === option.value,
                        'request-handling-step__option--disabled': isOptionDisabled(option),
                    }"
                    :title="optionDisabledMessage(option)"
                >
                    <input
                        :id="option.id"
                        v-model="selectedStrategy"
                        class="request-handling-step__radio"
                        type="radio"
                        name="request-handling-strategy"
                        :value="option.value"
                        :disabled="isOptionDisabled(option)"
                        @change="handleChange"
                    >

                    <span class="request-handling-step__icon" aria-hidden="true">
                        <feather-icon :icon="option.icon" size="24" />
                    </span>

                    <span class="request-handling-step__copy">
                        <span class="request-handling-step__title">
                            {{ translate(option.titleKey, option.fallbackTitle) }}
                        </span>
                        <span
                            v-if="settings.showOptionDescriptions"
                            class="request-handling-step__description"
                        >
                            {{ translate(option.descriptionKey, option.fallbackDescription) }}
                        </span>
                    </span>
                </label>
            </b-col>
        </b-row>

        <p
            v-if="simpleStrategiesDisabled"
            class="request-handling-step__strategy-help text-less-muted"
        >
            {{ simpleStrategyDisabledMessage }}
        </p>

        <Overlay
            v-if="showProductSupplierDetails"
            :show="supplierTranslationsLoading"
        >
            <ProductSupplierDetails
                v-if="supplierTranslationsReady"
                :details="supplierDetails"
                :product-attributes="context.productAttributes"
                :show-forward-action="!hasForwardedRequests"
                :forward-disabled="forwardActionDisabled"
                @change="handleSupplierDetailsChange"
                @forward="forwardToSupplier"
            />
        </Overlay>

        <ProductStructureBuilder
            v-if="showProductStructure"
            :hierarchy="context.productHierarchy"
            :mode="productStructureMode"
            :show-mode-selector="productStructureSettings.showModeSelector"
            :loading="productStructureState.loading"
            :busy-node-key="productStructureState.busyNodeKey"
            :search-products="searchProducts"
            :load-component-details="loadComponentDetails"
            @mode-change="productStructureMode = $event"
            @add-component="handleProductStructureAction('add', $event)"
            @update-component="handleProductStructureAction('update', $event)"
            @remove-component="handleProductStructureAction('remove', $event)"
            @error="$emit('error', $event)"
        />

        <ForwardedRequestsStatus
            v-if="showForwardedRequests"
            :state="forwardedRequestsState"
            :show-forward-action="hasForwardedRequests && isComplexProduct"
            :forward-disabled="forwardActionDisabled"
            @refresh="refreshForwardedRequests"
            @forward="forwardToSupplier"
            @open="openForwardedRequest"
        />
    </div>
</template>

<script>
import { BCol, BRow } from 'bootstrap-vue'
import Overlay from '@core/components/common/Overlay.vue'
import {
    loadTranslationGroups,
    SUPPLIER_SELECTION_TRANSLATION_GROUP,
} from '@/libs/i18n'
import {
    getNameOrId,
    ProductIdentifiers,
    ProductNames,
    ProductType,
    RequestStatus,
} from '@/libs/product'
import ProductStructureBuilder from '@/views/product/components/product-structure/ProductStructureBuilder.vue'
import ForwardRequestModal from './ForwardRequestModal.vue'
import ForwardedRequestsStatus from './ForwardedRequestsStatus.vue'
import ProductSupplierDetails from './ProductSupplierDetails.vue'
import { createRequestHandlingValidation } from './request-handling-validation'
import {
    RequestHandlingStrategy,
    isRequestHandlingStrategyAllowed,
} from './request-handling-strategy'

const OPTIONS = Object.freeze([
    Object.freeze({
        id: 'request-handling-self',
        value: RequestHandlingStrategy.ANSWER_REQUEST_DIRECTLY,
        icon: 'UserIcon',
        titleKey: 'Page(ReceivedRequest):Component(RequestHandling):answer-myself-title',
        fallbackTitle: 'I will answer the request myself',
        descriptionKey: 'Page(ReceivedRequest):Component(RequestHandling):answer-myself-description',
        fallbackDescription: 'You provide all required information directly.',
    }),
    Object.freeze({
        id: 'request-handling-product-supplier',
        value: RequestHandlingStrategy.FORWARD_TO_PRODUCT_SUPPLIER,
        icon: 'TruckIcon',
        titleKey: 'Page(ReceivedRequest):Component(RequestHandling):product-supplier-title',
        fallbackTitle: 'Send the complete request to the product supplier',
        descriptionKey: 'Page(ReceivedRequest):Component(RequestHandling):product-supplier-description',
        fallbackDescription: 'The product supplier provides all information.',
    }),
    Object.freeze({
        id: 'request-handling-component-suppliers',
        value: RequestHandlingStrategy.COLLECT_FROM_COMPONENT_SUPPLIERS,
        icon: 'LayersIcon',
        titleKey: 'Page(ReceivedRequest):Component(RequestHandling):component-suppliers-title',
        fallbackTitle: 'Collect information from component suppliers',
        descriptionKey: 'Page(ReceivedRequest):Component(RequestHandling):component-suppliers-description',
        fallbackDescription: 'You build the product structure and assign suppliers.',
    }),
])

export default {
    name: 'RequestHandlingStep',
    components: {
        BCol,
        BRow,
        Overlay,
        ForwardRequestModal,
        ForwardedRequestsStatus,
        ProductStructureBuilder,
        ProductSupplierDetails,
    },
    props: {
        context: { type: Object, required: true },
        settings: { type: Object, required: true },
        runtimeState: { type: Object, required: true },
    },
    data() {
        const workflowState = this.context.requestHandling
            && this.context.requestHandling.workflowState

        return {
            options: OPTIONS,
            selectedStrategy: workflowState ? workflowState.strategy : null,
            supplierDetails: this.createSupplierDetails(),
            supplierTranslationsLoading: false,
            supplierTranslationsReady: false,
            supplierTranslationPromise: null,
            forwardModalVisible: false,
            productStructureMode: this.settings.productStructure
                ? this.settings.productStructure.mode
                : 'top-down',
        }
    },
    computed: {
        showProductSupplierDetails() {
            return this.settings.showProductSupplierDetails !== false
                && this.selectedStrategy === RequestHandlingStrategy.FORWARD_TO_PRODUCT_SUPPLIER
                && this.isStrategyAllowed(this.selectedStrategy)
        },
        workflowState() {
            return this.context.requestHandling && this.context.requestHandling.workflowState
                ? this.context.requestHandling.workflowState
                : { loaded: false, strategy: null }
        },
        forwardedRequestsState() {
            const state = this.context.requestHandling
                && this.context.requestHandling.forwardedRequests

            return state || { items: [], loaded: false, loading: false, error: null }
        },
        forwardModalState() {
            const state = this.context.requestHandling
                && this.context.requestHandling.forwardModal

            return state || {
                targets: [],
                loaded: false,
                loading: false,
                submitting: false,
                error: null,
            }
        },
        showEmailSettings() {
            return Boolean(this.$store && this.$store.getters.showSendEmailSettingsStep)
        },
        showForwardedRequests() {
            return this.settings.showForwardedRequests !== false
                && (
                    this.context.request.status === RequestStatus.FORWARDED_TO_SUPPLIER
                    || this.forwardedRequestsState.loading
                    || this.forwardedRequestsState.items.length > 0
                )
        },
        hasForwardedRequests() {
            return this.context.request.status === RequestStatus.FORWARDED_TO_SUPPLIER
                || this.forwardedRequestsState.items.length > 0
        },
        isComplexProduct() {
            const request = this.context.request || {}
            const product = this.context.product || {}

            return this.selectedStrategy === RequestHandlingStrategy.COLLECT_FROM_COMPONENT_SUPPLIERS
                || request.isComplexProduct === true
                || product.isComplex === true
        },
        supplierDetailsValid() {
            return this.supplierDetails.suppliers.length > 0
                && this.supplierDetails.name.trim().length > 0
        },
        forwardActionDisabled() {
            if (this.runtimeState.isSaving) {
                return true
            }
            if (this.selectedStrategy === RequestHandlingStrategy.FORWARD_TO_PRODUCT_SUPPLIER) {
                return !this.supplierDetailsValid
            }
            if (this.selectedStrategy === RequestHandlingStrategy.COLLECT_FROM_COMPONENT_SUPPLIERS) {
                return !this.productStructureState.loaded
                    || this.productStructureComponentCount === 0
            }

            return true
        },
        productStructureSettings() {
            return {
                mode: 'top-down',
                showModeSelector: false,
                requireAtLeastOneComponent: true,
                ...(this.settings.productStructure || {}),
            }
        },
        productStructureState() {
            return this.context.requestHandling
                ? this.context.requestHandling.productStructure
                : { loading: false, loaded: false, busyNodeKey: null }
        },
        showProductStructure() {
            return this.settings.showProductStructure === true
                && this.selectedStrategy === RequestHandlingStrategy.COLLECT_FROM_COMPONENT_SUPPLIERS
                && this.isStrategyAllowed(this.selectedStrategy)
        },
        productStructureComponentCount() {
            const hierarchy = this.context.productHierarchy || {}
            return Array.isArray(hierarchy.children) ? hierarchy.children.length : 0
        },
        productStructureResolutionPending() {
            return this.settings.showProductStructure === true
                && this.isComplexProduct
                && this.productStructureState.loaded !== true
        },
        simpleStrategiesDisabled() {
            return this.productStructureComponentCount > 0
                || this.productStructureResolutionPending
        },
        simpleStrategyDisabledMessage() {
            return this.translate(
                'Page(ReceivedRequest):Component(RequestHandling):remove-components-before-simple',
                'Remove all product components before selecting this option.',
            )
        },
        bulkStrategyDisabledMessage() {
            return this.translate(
                'Page(ReceivedRequest):Component(RequestHandling):strategy-unavailable-for-mass-reply',
                'This option is not available for mass replies.',
            )
        },
    },
    watch: {
        showProductSupplierDetails: {
            immediate: true,
            handler(visible) {
                if (visible) {
                    this.ensureSupplierTranslations()
                }
            },
        },
        'workflowState.strategy'(strategy) {
            this.selectedStrategy = strategy || null
            this.emitValidity()
        },
        'workflowState.loaded'() {
            this.emitValidity()
        },
        productStructureComponentCount() {
            this.emitValidity()
        },
        'productStructureState.loaded'() {
            this.emitValidity()
        },
    },
    mounted() {
        this.emitValidity()
    },
    methods: {
        ensureSupplierTranslations() {
            if (this.supplierTranslationsReady) {
                return Promise.resolve()
            }
            if (this.supplierTranslationPromise) {
                return this.supplierTranslationPromise
            }

            this.supplierTranslationsLoading = true
            this.supplierTranslationPromise = loadTranslationGroups([
                SUPPLIER_SELECTION_TRANSLATION_GROUP,
            ])
                .catch(error => {
                    this.$emit('error', error)
                })
                .then(() => {
                    this.supplierTranslationsReady = true
                })
                .finally(() => {
                    this.supplierTranslationsLoading = false
                    this.supplierTranslationPromise = null
                })

            return this.supplierTranslationPromise
        },
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        isOptionDisabled(option) {
            return !this.isStrategyAllowed(option.value)
                || (
                    option.value !== RequestHandlingStrategy.COLLECT_FROM_COMPONENT_SUPPLIERS
                    && this.simpleStrategiesDisabled
                )
        },
        isStrategyAllowed(strategy) {
            return isRequestHandlingStrategyAllowed(strategy, this.context, this.settings)
        },
        optionDisabledMessage(option) {
            if (!this.isStrategyAllowed(option.value)) {
                return this.bulkStrategyDisabledMessage
            }

            return this.isOptionDisabled(option) ? this.simpleStrategyDisabledMessage : null
        },
        emitValidity() {
            this.$emit(
                'validity-change',
                createRequestHandlingValidation(this.context, this.settings, {
                    strategy: this.selectedStrategy,
                    supplierDetails: this.supplierDetails,
                }),
            )
        },
        createSupplierDetails() {
            const product = this.context.product || this.context.request.product

            return {
                name: getNameOrId(product.names || [], ProductNames.SUPPLIERS_PRODUCT_NAME) || '',
                identifier: getNameOrId(
                    product.identifiers || [],
                    ProductIdentifiers.SUPPLIERS_PRODUCT_ID,
                ) || '',
                suppliers: [...(product.suppliers || [])],
            }
        },
        handleChange() {
            this.emitValidity()
            this.$emit('action', {
                type: 'request-handling.change',
                payload: { strategy: this.selectedStrategy },
            })
        },
        handleSupplierDetailsChange(details) {
            this.supplierDetails = details
            this.emitValidity()
            this.$emit('action', {
                type: 'request-handling.supplier-details.change',
                payload: details,
            })
        },
        async forwardToSupplier() {
            if (this.forwardActionDisabled) {
                return
            }
            await this.ensureSupplierTranslations()
            this.forwardModalVisible = true
            this.$emit('action', { type: 'request-handling.forward' })
        },
        submitForwardRequest(payload) {
            this.$emit('action', {
                type: 'request-handling.forward.submit',
                payload,
            })
        },
        refreshForwardedRequests() {
            this.$emit('action', { type: 'request-handling.forwarded-requests.refresh' })
        },
        openForwardedRequest(request) {
            this.$emit('action', {
                type: 'request-handling.forwarded-request.open',
                payload: { request },
            })
        },
        searchProducts(query) {
            return this.context.services.searchProductStructureComponents(query)
        },
        async loadComponentDetails(component) {
            const card = await this.context.services.getProductStructureComponent(component.id)

            return {
                name: card.title || component.title,
                type: card.type || component.type || ProductType.OWN,
                supplierProductName: getNameOrId(
                    card.names || [],
                    ProductNames.SUPPLIERS_PRODUCT_NAME,
                ) || '',
                supplierProductIdentifier: getNameOrId(
                    card.identifiers || [],
                    ProductIdentifiers.SUPPLIERS_PRODUCT_ID,
                ) || '',
                suppliers: [...(card.suppliers || [])],
            }
        },
        handleProductStructureAction(operation, payload) {
            this.$emit('action', {
                type: `request-handling.product-structure.${operation}`,
                payload,
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.request-handling-step {
    margin-bottom: -1rem;
}

.request-handling-step__option {
    display: flex;
    align-items: flex-start;
    min-height: 7.25rem;
    height: 100%;
    margin: 0;
    padding: 1.07rem 1.33rem;
    border: 1px solid #d8d6de;
    border-radius: .357rem;
    background: #fff;
    cursor: pointer;
    transition: border-color .15s ease, box-shadow .15s ease;
}

.request-handling-step__option:hover,
.request-handling-step__option--selected {
    border-color: #f58049;
}

.request-handling-step__option--selected {
    box-shadow: 0 0 0 1px rgba(245, 128, 73, .18);
}

.request-handling-step__option--disabled {
    background: #f8f8f8;
    cursor: not-allowed;
    opacity: .65;
}

.request-handling-step__option--disabled:hover {
    border-color: #d8d6de;
}

.request-handling-step__strategy-help {
    margin: .25rem 0 0;
}

.request-handling-step__radio {
    appearance: none;
    width: 1.07rem;
    height: 1.07rem;
    flex: 0 0 auto;
    margin: .37rem .95rem 0 0;
    border: 1px solid #b9b9c3;
    border-radius: 50%;
    background-color: #fff;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
    }

    &:checked {
        border-color: #f58049;
        background-color: #f58049;
        box-shadow: inset 0 0 0 .21rem #fff;
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 .2rem rgba(245, 128, 73, .2);
    }

    &:checked:focus-visible {
        box-shadow:
            inset 0 0 0 .21rem #fff,
            0 0 0 .2rem rgba(245, 128, 73, .2);
    }
}

.request-handling-step__icon {
    display: inline-flex;
    flex: 0 0 3.2rem;
    align-items: center;
    justify-content: center;
    width: 3.2rem;
    height: 3.2rem;
    margin-right: 1.07rem;
    border: 1px solid #ebe9f1;
    border-radius: 50%;
    color: #f58049;
}

.request-handling-step__copy {
    display: flex;
    min-width: 0;
    flex-direction: column;
    padding-top: .21rem;
}

.request-handling-step__title {
    color: #2f2c52;
    font-size: 1.07rem;
    font-weight: 600;
    line-height: 1.35;
}

.request-handling-step__description {
    margin-top: .47rem;
    color: var(--workflow-secondary-text-color, #6e6b7b);
    font-size: .98rem;
    line-height: 1.45;
}

</style>
